import type {
    DeliveryStatus,
    ProgressDetails,
    TransportOrderDetail,
    TransportOrderListItem,
} from 'api/generated/transportOrdersApi';

/** `Laredo → Temple`, the way the design labels a route. */
export const formatRoute = (order: TransportOrderListItem) => `${order.launchHub.name} → ${order.landingHub.name}`;

/**
 * Status shown in the Status column and offered by the In transit filter.
 *
 * The service reports a `deliveryStatus` while a vehicle is under way. The design adds a fourth
 * status, Delivered, for an order that has reached its landing hub, which the model expresses as the
 * `ARRIVED` state rather than as a delivery status.
 */
export type OrderStatus = DeliveryStatus | 'DELIVERED';

/** Wording the design uses per status. */
export const ORDER_STATUS_TEXT: Record<OrderStatus, string> = {
    ON_TIME: 'On time',
    DELIVERED: 'Delivered',
    DELAYED: 'Delayed',
    STOPPED: 'Stopped',
};

/**
 * Colour the design gives a status wherever it tints text rather than a label.
 *
 * Shared by the In transit table and the detail panel summary, so a delayed order is the same orange
 * in both and a stopped one the same red.
 */
export const ORDER_STATUS_TEXT_COLOR: Record<OrderStatus, string> = {
    ON_TIME: 'text-color-info',
    DELIVERED: 'text-color-success',
    DELAYED: 'text-color-warning',
    STOPPED: 'text-color-danger',
};

/** The statuses the In transit filter offers, in the order the design lists them. */
export const IN_TRANSIT_STATUSES = [
    'ON_TIME',
    'DELIVERED',
    'DELAYED',
    'STOPPED',
] as const satisfies readonly OrderStatus[];

/**
 * Only the fields a status is derived from, and typed loosely on purpose: the JSON backed mocks hold
 * plain strings and have to classify their rows exactly the way the pages do.
 */
export type OrderStatusSource = { state: string; deliveryStatus?: string };

/** Unknown delivery statuses fall through to no status rather than being displayed raw. */
const ORDER_STATUS_BY_DELIVERY_STATUS: Record<string, OrderStatus> = {
    ON_TIME: 'ON_TIME',
    DELAYED: 'DELAYED',
    STOPPED: 'STOPPED',
};

/** Status of an order, or undefined while it is still waiting to be dispatched. */
export const getOrderStatus = ({ state, deliveryStatus }: OrderStatusSource): OrderStatus | undefined => {
    if (state === 'ARRIVED') {
        return 'DELIVERED';
    }

    return deliveryStatus === undefined ? undefined : ORDER_STATUS_BY_DELIVERY_STATUS[deliveryStatus];
};

/**
 * Bucket the status filter narrows the list by.
 *
 * The design offers the four statuses above. An order that has not been dispatched has none of them,
 * so it needs a bucket of its own: it is what the list narrows to when no status is selected, which
 * is otherwise a selection nothing could match.
 */
export type OrderStatusBucket = OrderStatus | 'NOT_DISPATCHED';

/** Every order falls into exactly one bucket. */
export const getOrderStatusBucket = (order: OrderStatusSource): OrderStatusBucket =>
    getOrderStatus(order) ?? 'NOT_DISPATCHED';

/**
 * Only the distances a percentage is derived from, typed loosely so the mocks can order rows by the
 * same calculation the bar renders. Same reasoning as `OrderStatusSource`.
 */
export type ProgressSource = { traveledDistanceMeters: number; totalDistanceMeters: number };

/**
 * Share of the route already driven, 0-100.
 *
 * The service reports distances rather than a percentage, so the bar derives one. A zero total
 * would divide by zero, which is treated as nothing driven yet.
 */
export const getProgressPercent = (progressDetails: ProgressSource | undefined) => {
    if (progressDetails === undefined || progressDetails.totalDistanceMeters === 0) {
        return 0;
    }

    const percent = (progressDetails.traveledDistanceMeters / progressDetails.totalDistanceMeters) * 100;
    return Math.min(100, Math.max(0, Math.round(percent)));
};

/**
 * The service reports meters; the app is US only, so distances are shown in miles. Meters stay the
 * stored and transported value everywhere, and the conversion happens here at display time.
 */
const METERS_PER_MILE = 1609.344;

/** Distance in whole miles, for example `75 mi`. */
export const formatDistance = (meters: number) => `${Math.round(meters / METERS_PER_MILE)} mi`;

/** Distance still to drive, in whole miles. */
export const formatRemainingDistance = (progressDetails: ProgressDetails | undefined) =>
    progressDetails === undefined ? '' : formatDistance(progressDetails.remainingDistanceMeters);

/**
 * How long a journey took, for example `1h58min`, or `45min` when it stayed under the hour.
 *
 * The service reports seconds. The design writes them without a space and drops the hours entirely
 * rather than showing a leading `0h`.
 */
export const formatDuration = (seconds: number) => {
    const totalMinutes = Math.round(seconds / 60);
    const minutes = totalMinutes % 60;
    const hours = (totalMinutes - minutes) / 60;

    return hours === 0 ? `${minutes}min` : `${hours}h ${minutes}min`;
};

/** Status shown in the detail panel summary, accounting for delayed deliveries. */
export const getSummaryStatus = (order: TransportOrderDetail): OrderStatus | undefined => {
    if (order.deliveredAt === undefined) {
        return getOrderStatus(order);
    }

    return order.deliveryStatus === 'DELAYED' ? 'DELAYED' : 'DELIVERED';
};

const deliveredDurationSeconds = ({ actualDurationSeconds, dispatchedAt, deliveredAt }: TransportOrderDetail) => {
    if (actualDurationSeconds !== undefined) {
        return actualDurationSeconds;
    }

    if (dispatchedAt === undefined || deliveredAt === undefined) {
        return;
    }

    return (Date.parse(deliveredAt) - Date.parse(dispatchedAt)) / 1000;
};

const remainingDurationSeconds = ({ progressDetails }: TransportOrderDetail) => {
    if (progressDetails === undefined) {
        return;
    }

    const seconds = (Date.parse(progressDetails.eta) - Date.parse(progressDetails.updatedAt)) / 1000;

    return seconds <= 0 ? undefined : seconds;
};

/** ETA or delivered-at timestamp to show in the summary, absent for stopped orders. */
export const getSummaryTime = (order: TransportOrderDetail, status: OrderStatus) => {
    if (status === 'STOPPED') {
        return;
    }

    return order.deliveredAt ?? order.progressDetails?.eta;
};

/** Duration in seconds to show in the summary: remaining while in transit, actual once delivered. */
export const getSummaryDurationSeconds = (order: TransportOrderDetail, status: OrderStatus) => {
    if (status === 'STOPPED') {
        return;
    }

    return order.deliveredAt === undefined ? remainingDurationSeconds(order) : deliveredDurationSeconds(order);
};
